import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { STEP_UP_REQUIRED_MESSAGE } from '@/lib/access/step-up';
import { reauthenticateForStepUp } from '@/lib/access/step-up-client';
import { useToast } from '@/hooks/use-toast';
import logger from '@/lib/logging';

export function StepUpAlertDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const handleSignInAgain = async () => {
    setBusy(true);
    try {
      const outcome = await reauthenticateForStepUp();
      if (outcome === 'redirect') {
        return;
      }
      toast({
        title: 'Sign-in refreshed',
        description: 'Retry Delete or copy sign-in link within 5 minutes.',
      });
      onOpenChange(false);
    } catch (error) {
      logger.error('[step-up] Reauthentication failed', { error });
      toast({
        title: 'Could not refresh sign-in',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="step-up-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm it is you</AlertDialogTitle>
          <AlertDialogDescription>{STEP_UP_REQUIRED_MESSAGE}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button
            type="button"
            data-testid="step-up-sign-in-again"
            disabled={busy}
            onClick={() => void handleSignInAgain()}
          >
            {busy ? 'Signing in…' : 'Sign in again'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
